import { useState, useEffect } from "react";
import axios from "axios";
import { AlertTriangle, ChevronDown, ChevronRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { API } from "@/context/AuthContext";
import { inrShort } from "@/pages/Dashboard";

const DAYS = [90, 180, 365];
const fmtQ = (q) => Number(q || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });

export function SubcontractorAlert({ asOf, source, plant, onJump }) {
  const [minDays, setMinDays] = useState(180);
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    if (!asOf) return;
    let live = true;
    axios.get(`${API}/reports/stock-ageing/subcontractor-alert`, { params: { as_of: asOf, source, min_days: minDays, ...(plant ? { plant } : {}) } })
      .then((r) => { if (live) setData(r.data); }).catch(() => { if (live) setData(null); });
    return () => { live = false; };
  }, [asOf, source, plant, minDays]);
  if (!data) return null;
  const flagged = data.suppliers.filter((s) => s.old_value > 0.5);
  return (
    <Card className="border-amber-200 shadow-sm" data-testid="subcon-alert-card">
      <CardHeader className="pb-2 flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle className="text-base text-slate-900 flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-600" />Job work out for more than
            <select value={minDays} onChange={(e) => setMinDays(Number(e.target.value))} className="h-7 rounded-md border border-amber-300 bg-amber-50 px-1.5 text-sm font-semibold text-amber-900" data-testid="subcon-alert-days">
              {DAYS.map((d) => <option key={d} value={d}>{d} days</option>)}
            </select>
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">Material lying with subcontractors beyond {minDays} days since it was issued / received (FIFO by receipt). Chase returns or GRNs for these.</p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-xl font-bold text-amber-800 tabular-nums" data-testid="subcon-alert-old-value">{inrShort(data.old_value)}</p>
          <p className="text-xs text-slate-500">{data.flagged} of {data.suppliers.length} subcontractors · {data.total_value ? Math.round(data.old_value / data.total_value * 100) : 0}% of {inrShort(data.total_value)} job work</p>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {flagged.length === 0 && <p className="text-sm text-slate-400 py-3" data-testid="subcon-alert-empty">Nothing older than {minDays} days at any subcontractor.</p>}
        <div className="divide-y divide-slate-100">
          {flagged.map((s) => (
            <div key={s.supplier} data-testid={`subcon-alert-row-${s.supplier}`}>
              <button type="button" onClick={() => setOpen(open === s.supplier ? null : s.supplier)} className="w-full flex items-center gap-3 py-2 text-left hover:bg-amber-50/60 rounded-md px-1">
                {open === s.supplier ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                <span className="flex-1 min-w-0">
                  <span className="text-sm font-medium text-slate-800">{s.supplier_name || s.supplier}</span>
                  <span className="text-xs text-slate-400 ml-2 font-mono">{s.supplier}</span>
                  <span className="block text-xs text-slate-500">{s.old_items} of {s.items} items overdue · holds {inrShort(s.total_value)} in total</span>
                </span>
                <span className="w-40 h-2 rounded-full bg-slate-100 overflow-hidden hidden sm:block"><span className="block h-full bg-amber-500" style={{ width: `${Math.min(100, Math.round(s.old_share * 100))}%` }} /></span>
                <span className="text-sm font-semibold text-amber-800 tabular-nums w-24 text-right">{inrShort(s.old_value)}</span>
                <span className="text-xs text-slate-500 w-12 text-right">{Math.round(s.old_share * 100)}%</span>
              </button>
              {open === s.supplier && (
                <div className="ml-8 mb-2 rounded-md border border-slate-100 bg-slate-50/60 text-xs">
                  {s.materials.map((m) => (
                    <div key={`${m.material}-${m.plant}`} className="flex items-center gap-3 px-3 py-1.5 border-b border-slate-100 last:border-0">
                      <button type="button" onClick={() => onJump?.(m.material, s.supplier_name || s.supplier)} className="font-mono text-sky-700 hover:underline" data-testid={`subcon-alert-mat-${m.material}`}>{m.material}</button>
                      <span className="flex-1 truncate text-slate-600">{m.description}</span>
                      <span className="text-slate-500">{m.plant}</span>
                      <span className="text-slate-500 w-28 text-right">{fmtQ(m.old_qty)} {m.unit}</span>
                      <span className="text-slate-500 w-24 text-right">since {m.oldest || "—"}</span>
                      <span className="font-semibold text-slate-800 w-24 text-right tabular-nums">{inrShort(m.old_value)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
